import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { FileText, Loader2, Paperclip, Phone, PhoneMissed, Send, Smile, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useTeamMembers, type TeamMember } from '@/api/agents'
import { useProfile } from '@/api/profile'
import { useUploadSMSMedia, type UploadedSMSMedia } from '@/api/sms'
import {
  signalerFrappeCollegue,
  useEnvoyerMessageCollegue,
  useMarquerLuCollegue,
  useMessagesCollegue,
  type AppelCollegue,
  type MessageCollegue,
} from '@/api/messagesCollegues'
import { useSSE, type FrappeCollegueEvent } from '@/contexts/SSEContext'
import { useCallActions } from '@/hooks/useCallActions'
import { useSelectedCallerId } from '@/hooks/useSelectedCallerId'
import { Lightbox } from '@/components/ui/Lightbox'

const nomMembre = (m: TeamMember) => `${m.user_first_name || ''} ${m.user_last_name || ''}`.trim() || m.user_email
const initiales = (m: TeamMember) =>
  `${m.user_first_name?.[0] || ''}${m.user_last_name?.[0] || ''}`.toUpperCase() || (m.user_email[0] || '?').toUpperCase()

// Mêmes types et limites que les pièces jointes d'un texto : le fichier passe
// par le même téléversement (/api/sms/upload-media/).
const TYPES_PIECES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'audio/mpeg', 'audio/ogg', 'application/pdf']
const TYPES_VIDEOS = ['video/mp4', 'video/quicktime', 'video/webm', 'video/3gpp']
const MAX_PIECE = 5 * 1024 * 1024
const MAX_VIDEO = 75 * 1024 * 1024

/** Un signal toutes les 3 s au plus pendant la frappe ; affiché 5 s. */
const INTERVALLE_FRAPPE = 3000
const DUREE_FRAPPE = 5000

/** Le collègue écrit en ce moment dans ce fil (signal SSE, rien en base). */
function useCollegueEcrit(userId: number, moiId: number | null): boolean {
  const { onFrappeCollegue } = useSSE()
  const [jusqua, setJusqua] = useState(0)
  useEffect(() => setJusqua(0), [userId])
  useEffect(
    () =>
      onFrappeCollegue((f: FrappeCollegueEvent) => {
        if (f.user_id === userId && f.to_user_id === moiId) setJusqua(Date.now() + DUREE_FRAPPE)
      }),
    [onFrappeCollegue, userId, moiId],
  )
  useEffect(() => {
    if (!jusqua) return
    const id = setTimeout(() => setJusqua(0), Math.max(jusqua - Date.now(), 50))
    return () => clearTimeout(id)
  }, [jusqua])
  return jusqua > 0
}

interface Props {
  userId: number
  darkMode: boolean
  onClose: () => void
}

/**
 * Fil de messages avec un collègue, dans la zone principale de la Boîte de
 * réception : en-tête (nom, statut, appeler), messages, zone d'écriture.
 */
export function FilCollegue({ userId, darkMode, onClose }: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const { data: profile } = useProfile()
  const moiId = profile?.id ?? null
  const { data: membres = [] } = useTeamMembers()
  const membre = membres.find((m) => m.user_id === userId)
  const nom = membre ? nomMembre(membre) : ''
  const { makeOutboundCall } = useCallActions()
  const selectedCallerId = useSelectedCallerId()

  const fil = useMessagesCollegue(userId)
  const messages = useMemo(
    () => [...(fil.data?.pages ?? [])].reverse().flatMap((p) => p.items),
    [fil.data],
  )
  const ecrit = useCollegueEcrit(userId, moiId)
  const [image, setImage] = useState<string | null>(null)

  // Lu dès que le fil est ouvert et visible, et à chaque nouveau message reçu.
  const { mutate: marquerLu } = useMarquerLuCollegue()
  const nonLu = messages.some((m) => m.kind === 'message' && m.from_user_id === userId && !m.read_at)
  useEffect(() => {
    if (!nonLu) return
    const essayer = () => {
      if (document.visibilityState === 'visible') marquerLu(userId)
    }
    essayer()
    document.addEventListener('visibilitychange', essayer)
    return () => document.removeEventListener('visibilitychange', essayer)
  }, [nonLu, userId, marquerLu])

  // En bas à l'ouverture et à chaque nouveau message ; pas quand on charge
  // les messages d'avant.
  const zoneRef = useRef<HTMLDivElement>(null)
  const dernier = messages.length ? messages[messages.length - 1] : null
  const dernierId = dernier ? `${dernier.kind}-${dernier.id}` : ''
  useLayoutEffect(() => {
    const el = zoneRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [dernierId, ecrit])

  const dispo = !!membre && membre.status === 'ONLINE' && !membre.dnd_enabled
  const occupe = membre?.status === 'BUSY'
  const statut = !membre
    ? ''
    : membre.dnd_enabled && membre.status !== 'OFFLINE'
      ? t('messagesCollegues.statutNePasDeranger', 'Ne pas déranger')
      : dispo
        ? t('messagesCollegues.statutEnLigne', 'En ligne')
        : occupe
          ? t('messagesCollegues.statutOccupe', 'Occupé')
          : t('messagesCollegues.statutHorsLigne', 'Hors ligne')

  const iconButton = cn(
    'p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
    darkMode ? 'hover:bg-slate-800 text-slate-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700',
  )
  const discret = darkMode ? 'text-slate-500' : 'text-slate-400'

  const heure = (iso: string) => {
    const d = new Date(iso)
    const memeJour = d.toDateString() === new Date().toDateString()
    return d.toLocaleString(locale, memeJour ? { hour: '2-digit', minute: '2-digit' } : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
  }
  const dernierMien = [...messages].reverse().find((m) => m.kind === 'message' && m.from_user_id === moiId)

  return (
    <div className={cn('flex-1 flex flex-col h-full overflow-hidden min-w-0', darkMode ? 'bg-slate-950' : 'bg-slate-50')}>
      <header
        className={cn(
          'flex-shrink-0 flex items-center justify-between gap-2 px-3 py-2 border-b',
          darkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white',
        )}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="relative flex-shrink-0">
            <span
              className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold',
                darkMode ? 'bg-slate-700 text-slate-200' : 'bg-gray-200 text-gray-600',
              )}
            >
              {membre ? initiales(membre) : '?'}
            </span>
            {membre && (
              <span
                className={cn(
                  'absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2',
                  darkMode ? 'ring-slate-900' : 'ring-white',
                  dispo ? 'bg-emerald-500' : occupe ? 'bg-amber-500' : 'bg-slate-400',
                )}
              />
            )}
          </span>
          <div className="min-w-0">
            <p className={cn('text-[13px] font-medium truncate', darkMode ? 'text-white' : 'text-slate-900')}>{nom}</p>
            {statut && <p className={cn('text-[11px] truncate', discret)}>{statut}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            disabled={!membre || (!dispo && !occupe)}
            onClick={() =>
              membre &&
              void makeOutboundCall(`client:agent_${membre.user_id}_${membre.company}`, selectedCallerId || undefined, nom)
            }
            className={iconButton}
            title={t('phone.call', 'Appeler')}
          >
            <Phone className="h-4 w-4" />
          </button>
          <button type="button" onClick={onClose} className={iconButton} title={t('common.close', 'Fermer')}>
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div ref={zoneRef} className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2">
        {fil.hasNextPage && (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => void fil.fetchNextPage()}
              disabled={fil.isFetchingNextPage}
              className={cn('text-xs font-medium px-3 py-1 rounded-full', darkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100')}
            >
              {fil.isFetchingNextPage ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('messagesCollegues.precedents', 'Voir les messages précédents')}
            </button>
          </div>
        )}
        {fil.isLoading ? (
          <div className={cn('flex justify-center py-10', discret)}>
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <p className={cn('py-10 text-center text-sm', discret)}>
            {t('messagesCollegues.vide', 'Aucun message avec {{name}} pour le moment.', { name: nom })}
          </p>
        ) : (
          messages.map((m) =>
            m.kind === 'call' ? (
              <CarteAppel key={`call-${m.id}`} appel={m} heure={heure(m.created_at)} darkMode={darkMode} />
            ) : (
              <Bulle
                key={`message-${m.id}`}
                message={m}
                mien={m.from_user_id === moiId}
                heure={heure(m.created_at)}
                lu={m.id === dernierMien?.id && !!m.read_at}
                darkMode={darkMode}
                onImage={setImage}
              />
            ),
          )
        )}
      </div>

      {ecrit && (
        <p aria-live="polite" className={cn('flex-shrink-0 px-4 py-1 text-[11.5px] italic', darkMode ? 'text-slate-400' : 'text-slate-500')}>
          {t('inbox.team.typingOne', '{{name}} écrit…', { name: nom })}
        </p>
      )}

      <ZoneEcriture key={userId} userId={userId} nom={nom} darkMode={darkMode} />
      <Lightbox url={image} onClose={() => setImage(null)} />
    </div>
  )
}

/** Un appel interne dans le fil : carte compacte, au centre ; manqué en rouge. */
function CarteAppel({ appel, heure, darkMode }: { appel: AppelCollegue; heure: string; darkMode: boolean }) {
  const { t } = useTranslation()
  const duree = `${Math.floor(appel.duration / 60)}:${String(appel.duration % 60).padStart(2, '0')}`
  return (
    <div className="flex flex-col items-center">
      <div
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium',
          appel.missed
            ? darkMode
              ? 'border-red-500/30 bg-red-500/10 text-red-400'
              : 'border-red-200 bg-red-50 text-red-600'
            : darkMode
              ? 'border-slate-700 bg-slate-900 text-slate-300'
              : 'border-slate-200 bg-white text-slate-600',
        )}
      >
        {appel.missed ? <PhoneMissed className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
        {appel.missed
          ? t('messagesCollegues.appelManque', 'Appel interne manqué')
          : t('messagesCollegues.appel', 'Appel interne · {{duree}}', { duree })}
      </div>
      <span className={cn('mt-0.5 px-1 text-[10.5px]', darkMode ? 'text-slate-500' : 'text-slate-400')}>{heure}</span>
    </div>
  )
}

function Bulle({
  message,
  mien,
  heure,
  lu,
  darkMode,
  onImage,
}: {
  message: MessageCollegue
  mien: boolean
  heure: string
  lu: boolean
  darkMode: boolean
  onImage: (url: string) => void
}) {
  const { t } = useTranslation()
  const pj = message.attachment
  return (
    <div className={cn('flex flex-col', mien ? 'items-end' : 'items-start')}>
      <div
        className={cn(
          'max-w-[75%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words',
          mien
            ? 'bg-indigo-600 text-white rounded-br-md'
            : darkMode
              ? 'bg-slate-800 text-slate-100 rounded-bl-md'
              : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md',
        )}
      >
        {pj &&
          (pj.content_type.startsWith('image/') ? (
            <button type="button" onClick={() => onImage(pj.url)} className="block mb-1">
              <img src={pj.url} alt={pj.filename} className="max-h-56 rounded-lg object-cover" />
            </button>
          ) : pj.content_type.startsWith('audio/') ? (
            <audio controls src={pj.url} className="mb-1 max-w-full" />
          ) : (
            <a
              href={pj.url}
              target="_blank"
              rel="noreferrer"
              className={cn('mb-1 flex items-center gap-1.5 underline', mien ? 'text-white' : darkMode ? 'text-slate-200' : 'text-slate-700')}
            >
              <FileText className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">{pj.filename}</span>
            </a>
          ))}
        {message.text}
      </div>
      <span className={cn('mt-0.5 px-1 text-[10.5px]', darkMode ? 'text-slate-500' : 'text-slate-400')}>
        {heure}
        {lu && ` · ${t('messagesCollegues.lu', 'Lu')}`}
      </span>
    </div>
  )
}

/** Texte, emojis et une pièce jointe, comme le composer d'un texto. */
function ZoneEcriture({ userId, nom, darkMode }: { userId: number; nom: string; darkMode: boolean }) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const envoyer = useEnvoyerMessageCollegue(userId)
  const televerser = useUploadSMSMedia('collegue')
  const [texte, setTexte] = useState('')
  const [piece, setPiece] = useState<Extract<UploadedSMSMedia, { kind: 'attachment' }> | null>(null)
  const [emojis, setEmojis] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const fichierRef = useRef<HTMLInputElement>(null)
  const derniereFrappe = useRef(0)

  useEffect(() => inputRef.current?.focus(), [])

  const changer = (e: ChangeEvent<HTMLInputElement>) => {
    setTexte(e.target.value)
    const maintenant = Date.now()
    if (e.target.value.trim() && maintenant - derniereFrappe.current >= INTERVALLE_FRAPPE) {
      derniereFrappe.current = maintenant
      signalerFrappeCollegue(userId)
    }
  }

  const choisirFichier = async (e: ChangeEvent<HTMLInputElement>) => {
    const fichier = e.target.files?.[0]
    if (fichierRef.current) fichierRef.current.value = ''
    if (!fichier) return
    const video = TYPES_VIDEOS.includes(fichier.type)
    if (!video && !TYPES_PIECES.includes(fichier.type)) {
      toast.error(t('messagesCollegues.typeRefuse', 'Ce type de fichier ne peut pas être joint'))
      return
    }
    const limite = video ? MAX_VIDEO : MAX_PIECE
    if (fichier.size > limite) {
      toast.error(t('messagesCollegues.tropLourd', 'Fichier trop lourd ({{mo}} Mo au plus)', { mo: Math.round(limite / (1024 * 1024)) }))
      return
    }
    try {
      const envoye = await televerser.mutateAsync(fichier)
      // Une vidéo lourde revient sous forme de lien : il va dans le texte.
      if (envoye.kind === 'link') setTexte((prev) => (prev.trim() ? `${prev.trim()} ${envoye.share_url}` : envoye.share_url))
      else setPiece(envoye)
    } catch {
      toast.error(t('messagesCollegues.erreurFichier', "Le fichier n'a pas été joint"))
    }
  }

  const valider = () => {
    const contenu = texte.trim()
    if ((!contenu && !piece) || envoyer.isPending || televerser.isPending) return
    envoyer.mutate(
      {
        text: contenu,
        attachment: piece ? { s3_key: piece.s3_key, content_type: piece.content_type, filename: piece.filename } : undefined,
      },
      {
        onSuccess: () => {
          setTexte('')
          setPiece(null)
          inputRef.current?.focus()
        },
        onError: () => toast.error(t('messagesCollegues.erreurEnvoi', "Le message n'est pas parti")),
      },
    )
  }

  const touche = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      valider()
    }
  }

  const pret = !!texte.trim() || !!piece
  const bouton = (actif = false) =>
    cn(
      'flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all',
      actif ? 'text-indigo-400' : darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600',
    )

  return (
    <div className={cn('flex-shrink-0 border-t', darkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200')}>
      {piece && (
        <div className="px-4 pt-3">
          <div className="relative inline-block group/piece">
            {piece.content_type.startsWith('image/') ? (
              <img src={piece.url} alt={piece.filename} className="w-16 h-16 rounded-lg object-cover" />
            ) : (
              <div
                className={cn(
                  'w-16 h-16 rounded-lg flex flex-col items-center justify-center text-[10px]',
                  darkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500',
                )}
              >
                <FileText className="w-5 h-5 mb-0.5" />
                <span className="truncate max-w-[56px]">{piece.filename.split('.').pop()}</span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setPiece(null)}
              title={t('messagesCollegues.retirerPiece', 'Retirer la pièce jointe')}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
      <div className="px-4 py-3 relative">
        {emojis && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setEmojis(false)} />
            <div className="absolute bottom-full left-4 mb-2 z-50">
              <Picker
                data={data}
                onEmojiSelect={(emoji: { native: string }) => {
                  setTexte((prev) => prev + emoji.native)
                  setEmojis(false)
                  inputRef.current?.focus()
                }}
                theme={darkMode ? 'dark' : 'light'}
                locale={locale}
                previewPosition="none"
                skinTonePosition="search"
                set="native"
                maxFrequentRows={2}
                perLine={8}
              />
            </div>
          </>
        )}
        <div className={cn('flex items-center rounded-xl', darkMode ? 'bg-slate-900/80' : 'bg-gray-50')}>
          <input
            ref={fichierRef}
            type="file"
            accept={[...TYPES_PIECES, ...TYPES_VIDEOS].join(',')}
            onChange={choisirFichier}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fichierRef.current?.click()}
            disabled={televerser.isPending || envoyer.isPending || !!piece}
            className={cn('ml-2', bouton(), televerser.isPending && 'animate-pulse text-indigo-400', 'disabled:cursor-not-allowed')}
            title={t('sms.attachFile', 'Joindre un fichier')}
          >
            <Paperclip className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setEmojis((p) => !p)} className={bouton(emojis)} title="Emojis">
            <Smile className="w-4 h-4" />
          </button>
          <input
            ref={inputRef}
            type="text"
            value={texte}
            maxLength={4000}
            onChange={changer}
            onKeyDown={touche}
            disabled={envoyer.isPending}
            placeholder={t('messagesCollegues.placeholder', 'Écrire à {{name}}…', { name: nom })}
            className={cn(
              'flex-1 bg-transparent border-0 outline-none px-3 py-2.5 text-sm',
              darkMode ? 'text-white placeholder:text-slate-500' : 'text-slate-900 placeholder:text-slate-400',
            )}
          />
          <button
            type="button"
            onClick={valider}
            disabled={!pret || envoyer.isPending || televerser.isPending}
            title={t('messagesCollegues.envoyer', 'Envoyer')}
            className={cn(
              'flex-shrink-0 mr-2 w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:cursor-not-allowed',
              pret
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-sm shadow-indigo-600/20'
                : darkMode
                  ? 'text-slate-600'
                  : 'text-gray-300',
            )}
          >
            {envoyer.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  )
}
